import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@material-ui/core';
import { Box, Grid, InputAdornment, Typography } from '@mui/material';
import ClinicApi from 'api/master/clinic.api';
import MemberManagementApi from 'api/setting/member.api';
import HeaderCard from 'component/CardCustom/HeaderCard';
import InputCheckbox from 'component/Input/InputCheckbox';
import TableCustom from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import { KEY_STORAGE } from 'constants/common';
import { icons } from 'constants/images';
import { colors } from 'constants/theme';
import MainButton from 'new-components/buttons/main-button';
import TextInput from 'new-components/inputs/text-input';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { routeName } from 'routes/routes-name';
import { getBranch, setSecureItem } from 'utils/app.utils';
import { isCreate, isDelete, isUpdate } from 'utils/verification.utils';

const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
}

export default function MemberManagement() {
    const { t } = useTranslation()
    const history = useHistory()
    const [ranks, setRanks] = useState<any>([])
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('NO_SETTING')
    const [sortType, setSortType] = useState('ASC')
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(true)
    const [defaultRankId, setDefaultRankId] = useState<any>(0)


    const fetchData = async () => {
        try {
            setLoading(true)
            const response: any = await ClinicApi.getAutoIncremet(getBranch())
            if (response?.data?.autoIncrement) {
                setSortBy(response?.data.autoIncrement);
                setDefaultRankId(response?.data.defaultRankId)
                const resp = await MemberManagementApi.findAll({
                    page: page,
                    pageLimit: pageLimit,
                    sortBy: sortBy,
                    sortType: sortType,
                    search: search
                })
                if (resp.status === 200) {
                    if (response?.data.defaultRankId === null) setDefaultRankId(resp.data[0].rankId)
                    setRowCount(resp.headers['x-total']);
                    setRanks(resp.data);
                    setSecureItem(KEY_STORAGE.MEMBER_RANKING_TYPE, JSON.stringify(resp.data))
                }
            }
            setLoading(false)
        } catch (err) {
            setLoading(false)
            console.error("Error fetching auto increment data:", err);
        }
    }
    // const fetchData = useCallback(async () => {
    //     try {
    //         setLoading(true)
    //         const response = await ClinicApi.getAutoIncremet(getBranch());
    //         const data: any = response.data;
    //         if (data.autoIncrement) {
    //             setSortBy(data.autoIncrement);
    //             setDefaultRankId(data.defaultRankId)
    //             let condition: any = {};
    //             if (page) condition = { ...condition, page: page };
    //             if (pageLimit) condition = { ...condition, pageLimit: pageLimit };
    //             if (sortBy) condition = { ...condition, sortBy: sortBy };
    //             if (sortType) condition = { ...condition, sortType: sortType };
    //             if (search) condition = { ...condition, search: search };
    //             const res = await MemberManagementApi.findAll(condition);
    //             if (res.status === 200) {
    //                 if (data.defaultRankId === null) setDefaultRankId(res.data[0].rankId)
    //                 setRowCount(res.headers['x-total']);
    //                 setRanks(res.data);
    //                 localStorage.setItem(KEY_STORAGE.MEMBER_RANKING_TYPE, JSON.stringify(res.data))
    //             }
    //         }
    //         setLoading(false)
    //     } catch (error) {
    //         console.error("Error fetching auto increment data:", error);
    //     }
    // }, [page, pageLimit, sortBy, sortType, search]);

    useEffect(() => {
        fetchData();
    }, [page, pageLimit, sortBy, sortType]);


    const onRequestSort = (sortByVal: any, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const headCells = [
        { id: 'no', disablePadding: false, label: t('TABLE.NO') },
        { id: 'rankCode', disablePadding: false, label: t('MEMBER.TABLE.CELL.MEMBER_ID'), class: 'text-nowrap' },
        { id: 'rankName', disablePadding: false, label: t('MEMBER.TABLE.CELL.MEMBER_NAME'), class: 'text-nowrap' },
        { id: 'color', disablePadding: false, label: t('MEMBER.TABLE.CELL.COLOR'), class: 'text-nowrap' },
        { id: 'auto', disablePadding: false, label: t('MEMBER.TABLE.CELL.AUTO_PROMOTION'), class: 'text-nowrap' },
        { id: 'defaults', disablePadding: false, label: t('MEMBER.TABLE.CELL.IS_DEFAULT'), class: 'text-nowrap' },
        { id: 'price', disablePadding: false, label: t('MEMBER.TABLE.CELL.TOTAL_AMOUNT'), class: 'text-nowrap' },
        { id: 'detail', disablePadding: false, label: t('MEMBER.TABLE.CELL.DETAIL'), class: 'text-nowrap' },
        { id: 'updatedBy', disablePadding: false, label: t('MEMBER.TABLE.CELL.UPDATED_BY'), class: 'text-nowrap' }
    ]

    const renderData = (objData: any, no: number) => {
        no = page * pageLimit - pageLimit + no + 1
        const { rankId, rankCode, rankNameEn, color, totalPaid, serviceVisits, updatedBy, updatedAt, detail } = objData
        const mappings: { [key: string]: string } = {
            'TOTAL_SPENDING': `${totalPaid} ${t('MEMBER.UNIT.BATH')}`,
            'VISIT_COUNT': `${serviceVisits} ${t('MEMBER.UNIT.TIME')}`,
        };
        const objRenderData = {
            key: rankId,
            id: rankId,
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: no, class: 'text-nowrap' },
                { option: 'TEXT', align: 'left', label: rankCode, class: 'text-nowrap' },
                { option: 'TEXT', align: 'left', label: rankNameEn, class: 'text-nowrap' },
                {
                    option: 'COMPONENT', align: 'left', class: 'text-nowrap', component: (
                        <Box sx={{ background: `${color}`, height: '40px', width: '200px', border: '1px solid #C5C5C5' }} />
                    )
                },
                { option: 'TEXT', align: 'left', class: 'text-nowrap', label: t(`MEMBER.AUTO_PROMOTION.${sortBy}`) },
                {
                    option: 'COMPONENT', class: 'text-nowrap', align: 'center', component: (
                        <Box>
                            <InputCheckbox disabled checked={defaultRankId === rankId} />
                        </Box>
                    )
                },
                { option: 'TEXT', align: 'right', class: 'text-nowrap', label: mappings[sortBy] || '-' },
                {
                    option: 'COMPONENT', class: 'text-nowrap', component: (
                        <Tooltip title={detail} placement='top-start' >
                            <Typography sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '300px' }}>{detail}</Typography>
                        </Tooltip>
                    )
                },
                // { option: 'TEXT', class: '', label: detail || '-', style: { width: '200px' } },
                { option: 'UPDATE_BY', align: 'center', class: 'text-nowrap', label: { updatedBy: updatedBy, updatedAt: updatedAt } },

            ]
        }
        return (
            <TableRowCommon
                {...objRenderData}
            />
        )
    }

    return (
        <Box>
            <HeaderCard text={t('MEMBER.TITLE')} />
            <Box paddingX="24px" paddingTop="16px">
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            label={t('ค้นหา ชื่อสมาชิก')}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value)
                            }}
                            fullWidth
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={9} xl={10}>
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                type='primary'
                                variant='contained'
                                style={{ width: '120px' }}
                                onClick={() => {
                                    setPage(1)
                                    fetchData()
                                }} />

                            <MainButton
                                title={t('MEMBER.BUTTON.EDIT')}
                                disabled={permissions.isCreate.disabled}
                                type='primary' variant='contained'
                                onClick={() => history.push(routeName.memberManagement + '/update', { autoIncrement: sortBy, defaultRankId })}
                                startIcon={<img src={icons.iconFeatherEdit} alt="" style={{ width: 18, color: colors.white }} />} />
                        </Box>
                    </Grid>
                </Grid>

                <Box paddingTop="24px">
                    <TableCustom
                        page={page}
                        pageLimit={pageLimit}
                        sortType={sortType}
                        sortBy={sortBy}
                        onSort={onRequestSort}
                        setPageLimit={(limit) => setPageLimit(limit)}
                        setPage={(val) => setPage(val)}
                        rowCount={rowCount}
                        headCells={headCells}
                        rowsData={ranks.map((val: any, i: number) => {
                            return renderData(val, i)
                        })}
                    />
                </Box>
            </Box>
        </Box>
    )
}
